<template>
  <v-container class="pa-0">
    <v-row no-gutters class="mb-2" style="gap: 0.5rem">
      <v-col cols="auto">
        <base-button translation-path="button.addLayer" @click="addLayer"></base-button>
      </v-col>
      <v-col cols="auto">
        <base-button translation-path="button.addGroup" @click="addGroup"></base-button>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <draggable-treeview
          selectable
          selectable-key="visible"
          :items.sync="computedLayersAdded"
          item-children="layers"
        >
          <template #prepend="{ item }">
            <v-col cols="auto" class="pa-0" v-if="!item.styles">
              <v-row no-gutters align="center">
                <v-col cols="auto" class="pa-0 pt-1" v-if="!item.layers">
                  <img
                    v-if="item.style && item.style.externalGraphic"
                    width="20px"
                    height="20px"
                    :src="item.iconSrc"
                    class="ml-1 mt-1"
                  />
                  <legend-layer-list-icon v-else :color="item.color" :layer-type="item.layerType" />
                </v-col>
              </v-row>
            </v-col>
          </template>
          <template #label="{ item }">
            <v-col cols="auto" class="pa-0 pl-1">
              <v-row no-gutters align="center">
                <v-col
                  cols="auto"
                  class="pa-0 pt-1 text-body-2"
                  :class="{
                    'font-weight-bold': item.layers ? true : false,
                  }"
                >
                  {{ item.name }}
                </v-col>
              </v-row>
              <v-row v-if="item.styles">
                <v-col class="pa-0 pl-2 pb-2">
                  <legend-layers-list-styles
                    :default-style="item.style"
                    :styles="item.styles"
                    :layer-type="item.layerType"
                    :has-permission="true"
                  />
                </v-col>
              </v-row>
            </v-col>
          </template>
          <template #append="{ item }">
            <v-col cols="auto" class="pa-0">
              <v-row no-gutters align="center">
                <v-col cols="auto">
                  <dots-menu
                    v-if="item.hasDotsMenus"
                    :items="item.dotsMenus"
                    @delete="deleteLayer(item.layer_id)"
                    @deleteGroup="deleteGroup(item)"
                    :close-on-content-click="false"
                  />
                </v-col>
              </v-row>
            </v-col>
          </template>
        </draggable-treeview>
      </v-col>
    </v-row>
    <layers-adding
      :is-layers-adding-visible.sync="isAddLayersDialogVisible"
      :layers="layersAdded"
      :layersAll="layersAllArray"
      @addLayers="addLayersHandler"
      :loading="isAddingLayerLoading"
      admin
    />
    <main-input-dialog
      v-if="isAddGroupDialogLoaded"
      :is-visible.sync="isAddGroupDialogVisible"
      :title-translation="$i18n.t('dialog.addGroup')"
      agree-text="dialog.agreeAdd"
      @agree="addGroupHandler"
      :inputs="addGroupInputs"
    />
  </v-container>
</template>

<script>
import DotsMenu from '@/components/DotsMenu';
import DraggableTreeview from '@/components/DraggableTreeview';
import LegendLayersListStyles from '@/components/LegendLayersListStyles';
import LegendLayerListIcon from '@/components/LegendLayerListIcon';

export default {
  name: 'DefaultProjectSettingsLayers',
  components: {
    DotsMenu,
    LayersAdding: () => import('@/components/LayersAdding'),
    DraggableTreeview,
    LegendLayersListStyles,
    LegendLayerListIcon,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    layersAdded: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    groupUniqueId: 0,
    isAddingLayerLoading: false,
    menuItems: [
      {
        name: 'delete',
      },
    ],
    isAddLayersDialogVisible: false,
    isAddGroupDialogVisible: false,
    isAddGroupDialogLoaded: false,
  }),
  computed: {
    addGroupInputs() {
      return [
        {
          name: 'name',
          value: '',
          translationPath: 'dialog.groupName',
          cols: '12',
          rules: [
            v => !!v || v == '0' || 'rules.required',
            v => !this.groupsNames.includes(v) || 'rules.alreadyGroupWithName',
          ],
          dataType: {
            name: 'text',
          },
        },
      ];
    },
    groups() {
      return this.$_getFlatGroupsLayers(this.computedLayersAdded || []).groups;
    },
    groupsNames() {
      return this.groups.map(group => group.name);
    },
    layersAllArray() {
      return this.$_objectToArray(this.$store.get('layers/layers'));
    },
    computedLayersAdded: {
      get() {
        return JSON.parse(JSON.stringify(this.layersAdded || [])).map(element => {
          if (element.layers) {
            element.layers = element.layers.map(layer => {
              return this.getLegendLayer(layer);
            });
            return this.getLegendGroup(element);
          } else {
            return this.getLegendLayer(element);
          }
        });
      },
      set(nV) {
        let elements = JSON.parse(JSON.stringify(nV));
        elements.map(element => {
          if (element.layers) {
            element.layers = element.layers.map(layer => {
              return this.setLegendLayer(layer);
            });
            return this.setLegendGroup(element);
          } else {
            return this.setLegendLayer(element);
          }
        });
        this.emitLayers(elements);
      },
    },
  },
  methods: {
    addGroupHandler() {
      const value = JSON.parse(JSON.stringify(this.addGroupInputs.find(input => input.name === 'name').value));
      const groupToAdd = {
        name: value,
        layers: [],
        type: 'group',
        id: `ugi-${this.groupUniqueId}`,
      };
      this.groupUniqueId++;
      this.computedLayersAdded = [...this.computedLayersAdded, ...[groupToAdd]];
      this.isAddGroupDialogVisible = false;
      this.addGroupInputs.find(input => input.name === 'name').value = '';
      this.$forceUpdate();
    },
    addLayer() {
      this.isAddLayersDialogVisible = true;
    },
    addGroup() {
      this.isAddGroupDialogLoaded = true;
      this.isAddGroupDialogVisible = true;
    },
    addLayersHandler(layers) {
      this.isAddingLayerLoading = true;
      const layersToAdd = layers.map(layer => {
        const { type, id } = layer;
        const layerToAdd = {
          layer_id: id,
          visible: true,
          opacity: 1,
          style: type !== 'service_layer' ? layer.style_web : undefined,
          labels_visible: type === 'features_layer' ? false : undefined,
          ...layer,
        };
        return layerToAdd;
      });
      this.computedLayersAdded = [...this.computedLayersAdded, ...layersToAdd];
      this.isAddingLayerLoading = false;
      this.isAddLayersDialogVisible = false;
    },
    deleteLayer(id) {
      const { parentIndex, index } = this.$_findLayerIndexByLayerId(this.computedLayersAdded, id);
      if (!parentIndex && parentIndex !== 0) {
        this.computedLayersAdded = this.computedLayersAdded.filter(l => l.layer_id !== id);
        return;
      }
      const layersAdded = JSON.parse(JSON.stringify(this.computedLayersAdded));
      this.$delete(layersAdded[parentIndex].layers, index);
      this.computedLayersAdded = layersAdded;
    },
    deleteGroup(group) {
      const stringifyGroup = JSON.stringify({
        name: group.name,
        layers: group.layers.map(layer => {
          return { id: layer.layer_id };
        }),
      });
      this.computedLayersAdded = this.computedLayersAdded.filter(group => {
        const { name, layers } = group;
        return (
          JSON.stringify({
            name,
            layers: (layers || []).map(layer => {
              return { id: layer.layer_id };
            }),
          }) !== stringifyGroup
        );
      });
    },
    getLegendGroup(group) {
      const selectableDisabled = group.layers.length < 1;
      return {
        ...group,
        dotsMenus: [
          {
            name: 'deleteGroup',
          },
        ],
        hasDotsMenus: true,
        selectableDisabled,
      };
    },
    getLegendLayer(layer) {
      const dotsMenus = [
        {
          name: 'delete',
        },
      ];
      return {
        ...layer,
        iconSrc: this.getIcon(layer.style ? layer.style.externalGraphic : null),
        color: this.getIconColor(layer.style),
        layerType: this.getLayerType(layer),
        styles: this.getLayerStylesHandler(layer),
        dotsMenus,
        hasDotsMenus: dotsMenus?.length > 0,
      };
    },
    setLegendGroup(group) {
      const list = ['dotsMenus', 'hasDotsMenus', 'selectableDisabled'];
      for (const l of list) {
        delete group[l];
      }
      return group;
    },
    setLegendLayer(layer) {
      const list = ['iconSrc', 'color', 'layerType', 'styles', 'dotsMenus', 'hasDotsMenus'];
      for (const l of list) {
        delete layer[l];
      }
      return layer;
    },
    getLayerType(layer) {
      return layer.type === 'features_layer' ? layer.geometry_type : layer.service_type;
    },
    getIconColor(style) {
      if (style) {
        return style['fill-color'] || style['circle-color'] || style['line-color'] || 'green';
      } else {
        return 'grey';
      }
    },
    getIcon(name) {
      return `${process.env.VUE_APP_API_URL}/icons/${name}`;
    },
    getLayerStylesHandler(layer) {
      if (!layer.style || (!layer.style.uniques && !layer.style.ranges)) {
        return;
      }
      const styles = layer.style.uniques ? this.$_objectToArray(layer.style.uniques.values) : layer.style.ranges.values;
      return this.getLayerStyles(styles);
    },
    getLayerStyles(stylesValues) {
      return stylesValues.map(value => {
        return {
          ...value,
          key: value.label || value.label === '' ? value.label : value.value,
          color: this.getIconColor(value),
          icon: value.externalGraphic && this.getIcon(value.externalGraphic),
        };
      });
    },
    emitLayers(layers) {
      this.$emit('layersAdded', layers);
    },
  },
};
</script>

<style>
.layer-element {
  border: 1px solid rgba(0, 0, 0, 0.12);
  text-align: left;
  height: 45px;
  padding-right: 7px;
}
.layer-element-title {
  font-family: Roboto !important;
  font-style: normal !important;
  font-weight: normal !important;
  font-size: 12px !important;
  line-height: 16px !important;
}
</style>
